import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CopyButton } from '../components/CopyButton';
import { LoadingDots } from '../components/LoadingDots';
import { apiFetch } from '../lib/api';

interface ManifestFile {
  name: string;
  url: string;
  size: number;
  sha256: string;
}

interface Manifest {
  version: string;
  messageCount: number;
  updated: string;
  files: ManifestFile[];
}

function formatKB(bytes: number): string {
  return `${(bytes / 1024).toFixed(1)} KB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 10);
}

const HELLO_EMRG_SNIPPET = `#include "emrg.h"
#include <stdio.h>

int main(void) {
  emrg_payload_t p = {0};
  p.category    = EMRG_CAT_FIRE;
  p.severity    = EMRG_SEV_WARNING;
  p.status      = EMRG_STATUS_NEW;
  p.device_id   = 42;
  p.timestamp   = 1712345678;
  p.has_gps     = true;
  p.lat         = emrg_gps_from_double(39.978371);
  p.lng         = emrg_gps_from_double(-86.118097);
  p.message_ref = 1;

  emrg_encoded_t out;
  if (emrg_encode(&p, 0, &out) == 0) {
    for (int i = 0; i < out.length; i++)
      printf("%02x", out.bytes[i]);
    printf("\\n");
  }
  return 0;
}`;

const GCC_SNIPPET = `gcc -std=c11 -Wall your_app.c emrg.c -o your_app`;

const VALIDATE_CURL = `curl -X POST https://astdz-api.astdz.workers.dev/v1/validate \\
  -H "Content-Type: application/json" \\
  -d '{"payload":"YOUR_HEX_HERE"}'`;

/**
 * SdkPage — auth-gated dashboard companion to the public `/docs/c-codec` page.
 *
 * Route: `/dashboard/sdk` (rendered inside `DashboardLayout`, requires auth).
 * Purpose: lets logged-in users download the EMRG embedded C SDK (emrg.h, emrg.c,
 * codec-c-latest.zip) directly from `app.astdz.com/codec-c/...`, view file
 * checksums and sizes from the runtime-fetched manifest, and follow install +
 * usage instructions for Arduino, PlatformIO, and bare gcc/cmake toolchains.
 */
export function SdkPage() {
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [manifestError, setManifestError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [jsBusy, setJsBusy] = useState(false);
  async function downloadJs() { setJsBusy(true); setDownloadError(null); try { const source = await apiFetch<string>('/v1/content/codec.mjs', { responseType: 'text' }); const url = URL.createObjectURL(new Blob([source], { type: 'text/javascript' })); const a = document.createElement('a'); a.href = url; a.download = 'astdz-codecs.mjs'; document.body.append(a); a.click(); a.remove(); window.setTimeout(() => URL.revokeObjectURL(url), 1000); } catch (e) { setDownloadError(e instanceof Error ? e.message : 'download failed'); } finally { setJsBusy(false); } }

  useEffect(() => {
    let cancelled = false;
    fetch('/codec-c/manifest.json')
      .then((r) => {
        if (!r.ok) throw new Error(`manifest fetch failed (${r.status})`);
        return r.json();
      })
      .then((data: Manifest) => { if (!cancelled) setManifest(data); })
      .catch((e: Error) => { if (!cancelled) setManifestError(e.message); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div>
      <div className="text-muted" style={{ marginBottom: 8 }}>── EMBEDDED SDK ──────────────────────────────────────────</div>
      <p className="text-muted" style={{ marginBottom: 16 }}>
        JavaScript reference codecs support all eleven standards. The embedded C SDK below supports EMRG only.
      </p>
      <div className="box" style={{ marginBottom: 16 }}><strong>JavaScript reference codec</strong><p className="text-muted">Download once, then encode and decode offline. Start with complete examples in the <Link to="/dashboard/console">family console</Link>. Specifications remain drafts; software tests do not certify hardware or operational decisions.</p><button type="button" className="btn btn-primary" disabled={jsBusy} onClick={downloadJs}>{jsBusy ? 'downloading…' : '[download astdz-codecs.mjs]'}</button>{downloadError && <p className="text-danger" role="alert">JavaScript codec download failed ({downloadError}).</p>}</div>

      <div className="text-muted" style={{ marginBottom: 8 }}>── downloads ─────────────────────────────────────────────</div>
      <div className="box" style={{ marginBottom: 16 }}>
        {!manifest && !manifestError && <LoadingDots />}
        {manifestError && (
          <p className="text-danger">
            could not load manifest ({manifestError}). see <Link to="/docs/c-codec">/docs/c-codec</Link> for the public download page.
          </p>
        )}
        {manifest && (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-muted)', fontWeight: 500 }}>file</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-muted)', fontWeight: 500 }}>size</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-muted)', fontWeight: 500 }}>sha256</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-muted)', fontWeight: 500 }}>actions</th>
                </tr>
              </thead>
              <tbody>
                {manifest.files.map((f) => {
                  const absUrl = typeof window !== 'undefined' ? window.location.origin + f.url : f.url;
                  return (
                    <tr key={f.name}>
                      <td style={{ padding: '4px 8px', borderBottom: '1px dashed var(--color-border)' }}>{f.name}</td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px dashed var(--color-border)', color: 'var(--color-muted)' }}>{formatKB(f.size)}</td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px dashed var(--color-border)' }}>
                        <span className="mono" style={{ color: 'var(--color-muted)' }}>{f.sha256.slice(0, 12)}…</span>{' '}
                        <CopyButton text={f.sha256} label="copy hash" />
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px dashed var(--color-border)' }}>
                        <a href={f.url} download className="btn btn-primary" style={{ marginRight: 8 }}>[download]</a>
                        <CopyButton text={absUrl} label="copy URL" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-muted" style={{ marginTop: 12, fontSize: 'var(--font-size-sm)' }}>
              version {manifest.version} · {manifest.messageCount} messages · updated {formatDate(manifest.updated)}
            </p>
          </>
        )}
      </div>

      <div className="text-muted" style={{ marginBottom: 8 }}>── what you get ──────────────────────────────────────────</div>
      <div className="box" style={{ marginBottom: 16 }}>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li>emrg_encode() / emrg_decode() / emrg_validate()</li>
          <li>all three tiers: NANO (51B), MICRO (115B), FULL (222B)</li>
          <li>180 pre-defined messages across 15 emergency categories</li>
          <li>CRC16-CCITT integrity checking</li>
          <li>7-bit GSM-style text packing</li>
          <li>GPS encoding helpers</li>
          <li>human-readable lookup tables (categories, severities, etc.)</li>
        </ul>
      </div>

      <div className="text-muted" style={{ marginBottom: 8 }}>── install — three ways ──────────────────────────────────</div>
      <div className="box" style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <div className="text-muted" style={{ marginBottom: 4 }}>arduino ide</div>
          <pre className="hex-display" style={{ whiteSpace: 'pre', overflowX: 'auto', margin: 0 }}>{`1. download emrg.h and emrg.c above
2. drop both files into your sketch folder
3. add #include "emrg.h" to your sketch
4. compile and upload`}</pre>
          <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginTop: 4 }}>
            see the full arduino example inside codec-c-latest.zip at examples/arduino/emrg_example/
          </p>
        </div>

        <hr className="divider" />

        <div style={{ marginBottom: 16 }}>
          <div className="text-muted" style={{ marginBottom: 4 }}>platformio</div>
          <pre className="hex-display" style={{ whiteSpace: 'pre', overflowX: 'auto', margin: 0 }}>{`1. download codec-c-latest.zip and extract it
2. copy src/ contents into your project's lib/codec-c/
3. include in your code: #include <emrg.h>
4. platformio picks it up automatically`}</pre>
          <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginTop: 4 }}>
            see the full platformio example inside the zip at examples/platformio/
          </p>
        </div>

        <hr className="divider" />

        <div>
          <div className="text-muted" style={{ marginBottom: 4 }}>bare gcc / cmake</div>
          <pre className="hex-display" style={{ whiteSpace: 'pre', overflowX: 'auto', margin: 0 }}>{GCC_SNIPPET}</pre>
          <div style={{ marginTop: 4 }}>
            <CopyButton text={GCC_SNIPPET} label="copy" />
          </div>
          <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginTop: 4 }}>
            works on linux, macos, windows (mingw or msvc), and any embedded toolchain that speaks c11.
          </p>
        </div>
      </div>

      <div className="text-muted" style={{ marginBottom: 8 }}>── hello, EMRG ───────────────────────────────────────────</div>
      <div className="box" style={{ marginBottom: 16 }}>
        <pre className="hex-display" style={{ whiteSpace: 'pre', overflowX: 'auto', margin: 0 }}>{HELLO_EMRG_SNIPPET}</pre>
        <div style={{ marginTop: 8 }}>
          <CopyButton text={HELLO_EMRG_SNIPPET} label="copy code" />
        </div>
      </div>

      <div className="text-muted" style={{ marginBottom: 8 }}>── verify your firmware ──────────────────────────────────</div>
      <div className="box" style={{ marginBottom: 16 }}>
        <p style={{ marginTop: 0 }}>
          the api exposes a free, no-auth /v1/validate endpoint. post your encoded hex,
          get a structural pass/fail back. no signup required.
        </p>
        <pre className="hex-display" style={{ whiteSpace: 'pre', overflowX: 'auto', margin: 0 }}>{VALIDATE_CURL}</pre>
        <div style={{ marginTop: 8 }}>
          <CopyButton text={VALIDATE_CURL} label="copy curl" />
        </div>
        <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginTop: 8 }}>
          if your firmware produces hex that this endpoint validates as structurally sound,
          you're byte-compatible with the reference.
        </p>
      </div>

      <div className="text-muted" style={{ marginBottom: 8 }}>── hardware compatibility ────────────────────────────────</div>
      <div className="box" style={{ marginBottom: 16 }}>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li>ESP32 family — heltec wifi lora 32 v3, lilygo t-beam, m5stack atom</li>
          <li>RAK wisblock — nrf52840 + sx1262</li>
          <li>STM32 cortex-m — any -m0 / m3 / m4 / m7 with a c11 toolchain</li>
          <li>arduino uno r4 — renesas ra4m1</li>
          <li>raspberry pi pico — rp2040</li>
          <li>bare avr — atmega328p (yes, even an arduino uno r3)</li>
          <li>linux / macos / windows — for host-side testing</li>
        </ul>
        <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginTop: 12 }}>
          requires only stdint.h, stdbool.h, string.h. no malloc. no heap. no rtos. no dependencies.
        </p>
      </div>

      <div className="text-muted" style={{ marginBottom: 8 }}>── support ───────────────────────────────────────────────</div>
      <p style={{ marginBottom: 16 }}>
        questions or integration help — see <Link to="/dashboard/support">support</Link> or email{' '}
        <a href="mailto:support@astdz.com">support@astdz.com</a>
      </p>

      <div className="text-muted" style={{ marginBottom: 8 }}>── license ───────────────────────────────────────────────</div>
      <p>
        for use with the AstdZ™ platform under the <Link to="/terms">terms of service</Link>.
      </p>
    </div>
  );
}
